import { Component, lazy, Suspense, useEffect, useState, type ComponentType, type ErrorInfo, type ReactNode } from "react";
import { Layout } from "./components/Layout";
import { FullPageLoading } from "./components/Ui";
import { useAuth } from "./auth";
import { useRouter } from "./router";
import { demoMode } from "./mode";
import { api } from "./api";
import { SiteProvider } from "./site";
import type { ProvisionedSite } from "@juicytraffic/contracts";

const LoginPage = lazy(async () => ({ default: (await import("./pages/Login")).LoginPage }));
const OverviewPage = lazy(async () => ({
  default: (await import("./pages/Overview")).OverviewPage,
}));
const TrafficPage = lazy(async () => ({ default: (await import("./pages/Traffic")).TrafficPage }));
const RulesPage = lazy(async () => ({ default: (await import("./pages/Rules")).RulesPage }));
const ProtectionRulesPage = lazy(async () => ({ default: (await import("./pages/ProtectionRules")).ProtectionRulesPage }));
const ChannelsPage = lazy(async () => ({ default: (await import("./pages/Channels")).ChannelsPage }));
const UsagePage = lazy(async () => ({ default: (await import("./pages/Usage")).UsagePage }));
const IpExclusionsPage = lazy(async () => ({
  default: (await import("./pages/IpExclusions")).IpExclusionsPage,
}));
const AdminPage = lazy(async () => ({ default: (await import("./pages/Admin")).AdminPage }));
const ProfilePage = lazy(async () => ({ default: (await import("./pages/Profile")).ProfilePage }));
const SiteOnboardingPage = lazy(async () => ({ default: (await import("./pages/SiteOnboarding")).SiteOnboardingPage }));
const SitesPage = lazy(async () => ({ default: (await import("./pages/Sites")).SitesPage }));

function isChunkLoadError(error: unknown) {
  if (!(error instanceof Error)) return false;
  return /dynamically imported module|loading chunk|chunkloaderror/i.test(`${error.name} ${error.message}`);
}

class ChunkLoadRecovery extends Component<{ children: ReactNode }, { error: Error | null }> {
  state = { error: null };

  static getDerivedStateFromError(error: Error) {
    return { error };
  }

  componentDidCatch(error: Error, _info: ErrorInfo) {
    if (!isChunkLoadError(error)) return;

    // A previously opened tab can reference chunks removed by a newer deploy.
    // Reload once per entry module, then leave a visible fallback if it persists.
    const retryKey = `juicytraffic.chunk-load-recovery:${import.meta.url}`;
    try {
      if (window.sessionStorage.getItem(retryKey)) return;
      window.sessionStorage.setItem(retryKey, "1");
      window.location.reload();
    } catch {
      // Storage may be disabled; the fallback still gives the user a recovery path.
    }
  }

  render() {
    if (this.state.error) {
      return <div className="page-loading" role="alert">A newer version is available. Please refresh this page.</div>;
    }
    return this.props.children;
  }
}

export function App() {
  const { user, loading } = useAuth();
  const { path, search } = useRouter();
  const adminTenantId = new URLSearchParams(search).get("admin_tenant")?.trim() || "";
  const accountScope = JSON.stringify([user?.uid, adminTenantId]);
  const [loadedAccountScope, setLoadedAccountScope] = useState<string | null>(null);
  const [siteState, setSiteState] = useState<"loading" | "empty" | "ready">("loading");
  const [sites, setSites] = useState<ProvisionedSite[]>([]);
  const [siteLoadError, setSiteLoadError] = useState(false);

  const loadSites = async () => {
    const { sites: nextSites } = await api.sites();
    setSites(nextSites);
    setSiteState(nextSites.length ? "ready" : "empty");
    setLoadedAccountScope(accountScope);
  };

  useEffect(() => {
    if (!user || demoMode) {
      setSiteState(demoMode ? "ready" : "loading");
      return;
    }
    let active = true;
    setSiteState("loading");
    setSites([]);
    setSiteLoadError(false);
    setLoadedAccountScope(null);
    api.sites()
      .then(({ sites: nextSites }) => { if (active) { setSites(nextSites); setSiteState(nextSites.length ? "ready" : "empty"); setLoadedAccountScope(accountScope); } })
      .catch(() => { if (active) { setSiteLoadError(true); setLoadedAccountScope(accountScope); } });
    return () => { active = false; };
  }, [user, accountScope]);

  if (loading) return <FullPageLoading label="Loading account…" />;
  if (!user) {
    return (
      <ChunkLoadRecovery>
        <Suspense fallback={<FullPageLoading label="Loading…" />}>
          <LoginPage />
        </Suspense>
      </ChunkLoadRecovery>
    );
  }
  // Gate during render: effects run after children mount, which would otherwise
  // let the previous account's site issue requests under the new tenant header.
  if (!demoMode && loadedAccountScope !== accountScope) return <FullPageLoading label="Loading websites…" />;
  if (!demoMode && siteLoadError) return <div className="page-loading" role="alert">We couldn’t load your websites. Please refresh this page.</div>;
  if (!demoMode && siteState === "loading") return <FullPageLoading label="Loading websites…" />;
  if (!demoMode && siteState === "empty" && path !== "/usage") return (
    <ChunkLoadRecovery>
      <Suspense fallback={<FullPageLoading label="Loading setup…" />}>
        <SiteOnboardingPage onCreated={loadSites} />
      </Suspense>
    </ChunkLoadRecovery>
  );
  const pages: Record<string, ComponentType> = {
    "/": OverviewPage,
    "/click-events": TrafficPage,
    "/ip-exclusions": IpExclusionsPage,
    "/rules": RulesPage,
    "/protection-rules": ProtectionRulesPage,
    "/channels": ChannelsPage,
    "/usage": UsagePage,
    "/admin": AdminPage,
    "/profile": ProfilePage,
  };
  // Deep links in the public demo must be held to the same limited journey as
  // its navigation. This keeps billing, setup, and administration private.
  const demoPages = new Set(["/", "/click-events", "/acquisition", "/protection-rules", "/channels"]);
  const Page = demoMode
    ? demoPages.has(path) ? pages[path] || OverviewPage : OverviewPage
    : pages[path] || OverviewPage;
  return (
    <SiteProvider sites={sites.length ? sites : demoMode ? [{ site_id: "site_demo", site_key: "demo", tenant_id: "org_demo", name: "demo.juicytraffic.com", allowed_origins: ["https://demo.juicytraffic.com"], config_version: 1, updated_at: new Date().toISOString(), snippet: `<script async src="https://collect.juicytraffic.com/probe.js" data-site="demo"></script>` }] as ProvisionedSite[] : sites}>
    <Layout>
      <ChunkLoadRecovery>
        <Suspense fallback={<FullPageLoading label="Loading…" />}>
          {path === "/sites" && !demoMode ? <SitesPage sites={sites} onSitesChanged={loadSites} /> : <Page />}
        </Suspense>
      </ChunkLoadRecovery>
    </Layout>
    </SiteProvider>
  );
}
